import React, { useState, useMemo } from 'react';

import { PlusOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';
import StockTransferOrderConceptModal from '@shared/stock_transfer_orders/StockTransferOrderConceptModal';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { stockTransferOrderService } from '@services/operator/stockTransferOrderService';
import { MultipleValue } from '@shared/table/Rules/component';
import { formatDateTime } from '@utils/dateTime';
import { useMerchant } from '@contexts/operator/MerchantContext';
import { useIsStandard } from '@contexts/operator/PlanContext';

const STATUS_COLORS: Record<string, string> = {
  allocated: 'processing',
  out_of_stock: 'error',
  picking: 'orange',
  in_transit: 'blue',
  receiving: 'cyan',
  done: 'success',
  canceled: 'default'
};

interface StockTransferOrderRow {
  id: string;
  status?: string;
  barcode?: string;
  name?: string;
  merchant?: { id?: string; name?: string };
  source_warehouse?: { id?: string; name?: string };
  destination_warehouse?: { id?: string; name?: string };
  eta?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
}

export default function StockTransferOrdersList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);
  const { merchants } = useMerchant() as { merchants: MerchantOption[] };
  const isStandard = useIsStandard();

  const columns = useMemo(() => [
    {
      nameKey: 'operator.stock_transfer_orders.columns.status',
      width: '120px',
      cell: ({ status }: StockTransferOrderRow) => (
        <Tag color={STATUS_COLORS[status || ''] || 'default'}>
          {t(`enums.stock_transfer_order_status.${status}`)}
        </Tag>
      )
    },
    {
      nameKey: 'models.stock_transfer_order.name',
      sortable: true,
      sortFieldName: 'stock_transfer_orders.name',
      cell: ({ id, barcode, name }: StockTransferOrderRow) => (
        <Link to={`/stock_transfer_orders/${id}`}>
          <span style={{ fontWeight: 'bold' }}>{name}</span>
          <div style={{ color: '#888', fontSize: '0.85em' }}>{barcode}</div>
        </Link>
      )
    },
    // 單一 merchant 模式下不顯示 merchant 欄位
    ...(isStandard ? [] : [{
      nameKey: 'operator.stock_transfer_orders.columns.merchant',
      selector: ({ merchant }: StockTransferOrderRow) => merchant?.name
    }]),
    {
      nameKey: 'operator.stock_transfer_orders.columns.source_warehouse',
      selector: ({ source_warehouse }: StockTransferOrderRow) => source_warehouse?.name
    },
    {
      nameKey: 'operator.stock_transfer_orders.columns.destination_warehouse',
      selector: ({ destination_warehouse }: StockTransferOrderRow) => destination_warehouse?.name
    },
    {
      nameKey: 'operator.stock_transfer_orders.columns.eta',
      sortable: true,
      sortFieldName: 'stock_transfer_orders.eta',
      cell: ({ eta }: StockTransferOrderRow) => eta || '-'
    },
    {
      nameKey: 'operator.stock_transfer_orders.columns.created_at',
      sortable: true,
      sortFieldName: 'stock_transfer_orders.created_at',
      cell: ({ created_at }: StockTransferOrderRow) => created_at ? formatDateTime(created_at) : '-'
    }
  ], [t, isStandard]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('operator.stock_transfer_orders.filters.status'),
      component: MultipleValue,
      inputName: 'status',
      defaultOptions: ['allocated', 'out_of_stock', 'picking', 'in_transit', 'receiving', 'done', 'canceled'].map(s => ({
        label: t(`enums.stock_transfer_order_status.${s}`),
        value: s
      })),
      createable: false
    },
    // 單一 merchant 模式下不顯示 merchant 篩選器
    ...(isStandard ? [] : [{
      label: t('operator.stock_transfer_orders.filters.merchant'),
      component: MultipleValue,
      inputName: 'merchant_id',
      defaultOptions: merchants.map((m: MerchantOption) => ({ label: m.name, value: m.id })),
      createable: false
    }]),
    {
      label: t('operator.stock_transfer_orders.filters.search'),
      component: MultipleValue,
      inputName: 'search',
      createable: true
    }
  ], [t, merchants, isStandard]);

  return (
    <div>
      <ListPageHeader
        items={['stock_transfer_orders', 'stock_transfer_orders.list']}
        guideButton={
          <Tooltip title={t('concepts.stock_transfer_orders.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
            />
          </Tooltip>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/stock_transfer_orders/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.stock_transfer_order.barcode'),
            t('models.stock_transfer_order.name'),
          ],
        }}
        tableId="StockTransferOrdersList"
        defaultQuery={{ sorting_field: 'stock_transfer_orders.created_at', sorting_direction: 'desc' }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={stockTransferOrderService}
      />
      <StockTransferOrderConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}
